/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { styled } from "@mui/material/styles";
import {
  Button,
  Card,
  CircularProgress,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import {
  GetAllSupportItem,
  UpdateSupportStatus,
  SupportFilesDownload,
} from "api-services";
import { useDispatch, useSelector } from "react-redux";
import MDBadge from "components/MDBadge";
import moment from "moment/moment";
import "./style.css";
import { CSVLink } from "react-csv";
import MDButton from "components/MDButton";
import { ShowSuccessMessage, ShowErrorMessage } from "helpers";
import { useMaterialUIController } from "context";

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    fontFamily: "inherit",
    textAlign: "left",
  },
});

function SupportRM() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const dispatchData = useDispatch();
  const { user_data } = useSelector((state) => state.authentication);
  //Download
  const [downloadData, setDownloadData] = useState(null);
  const [controller, dispatch] = useMaterialUIController();
  const [showErrorMessages, setShowErrorMessage] = useState("");

  const columns = [
    { Header: "Support ID", accessor: "id", align: "left" },
    { Header: "Full Name", accessor: "full_name", align: "left" },
    { Header: "Subject", accessor: "subject", align: "left" },
    { Header: "Message", accessor: "message", width: "40%", align: "left" },
    { Header: "Posted On", accessor: "posted_on", align: "left" },
    { Header: "Status", accessor: "status", align: "left" },
    { Header: "Attachment", accessor: "attachment", align: "center" },
    { Header: "Action", accessor: "action", align: "center" },
  ];

  useEffect(() => {
    GetAllSupportItem(dispatchData, successAction, errorAction);
  }, [dispatchData]);

  const successAction = (res) => {
    setDataLoading(false);
    const allSupportItem = res.data.map(mapSupportItem);
    setRows(allSupportItem);
    getDataForDownload(res.data);
  };

  const mapSupportItem = (item) => {
    const fullName = `${item.firstName} ${item.lastName || ""}`;
    return {
      id: renderId(item.id),
      full_name: renderAuthor(fullName, item.email),
      subject: renderTooltip(item.subject),
      message: renderTooltip(item.message),
      posted_on: renderPostedOn(item.postedOn),
      status: renderStatusBadge(item.status),
      attachment: renderAttachmentButton(item),
      action: renderActionButton(item),
    };
  };

  const renderId = (id) => (
    <MDTypography variant="caption" color="text" fontWeight="medium">
      {id}
    </MDTypography>
  );

  const renderAuthor = (fullName, email) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDBox lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {fullName}
        </MDTypography>
        <MDTypography variant="caption">{email}</MDTypography>
      </MDBox>
    </MDBox>
  );

  const renderTooltip = (text) => (
    <CustomWidthTooltip title={text} arrow>
      <MDTypography
        component="p"
        variant="caption"
        color="text"
        fontWeight="medium"
        className="max-length"
      >
        {text}
      </MDTypography>
    </CustomWidthTooltip>
  );

  const renderPostedOn = (postedOn) => (
    <MDTypography variant="caption" color="text" fontWeight="medium">
      {moment(postedOn).format("MM/DD/yyyy")}
    </MDTypography>
  );

  const renderStatusBadge = (status) => (
    <MDBox ml={-1}>
      <MDBadge
        badgeContent={status}
        color={status === "Unresolved" ? "error" : "success"}
        variant="gradient"
        size="sm"
      />
    </MDBox>
  );

  const handleDownloadButtonClick = (fileName) => {
    setLoading(true);

    SupportFilesDownload(
      dispatchData,
      (res) => {
        setLoading(false);

        // Create a Blob URL from the response data
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName); // Set the file name for download
        document.body.appendChild(link);
        link.click(); // Trigger the download
        link.remove(); // Clean up the link element
        ShowSuccessMessage(dispatch, "File downloaded successfully");
      },
      (err) => {
        setLoading(false);
        setShowErrorMessage(err.message);
        ShowErrorMessage(dispatch, "Error downloading file");
      },
      fileName
    );
  };

  const renderAttachmentButton = (item) =>
    item.fileName !== null && (
      <Button
        sx={{
          textTransform: "uppercase",
          borderRadius: "4px",
          color: "white !important",
          fontSize: "11px",
          width: "100px",
        }}
        variant="contained"
        size="small"
        download
        // onClick={() => window.open(item.filePath, "_blank")}
        onClick={() => handleDownloadButtonClick(item.fileName)}
      >
        Download
      </Button>
    );

  const renderActionButton = (item) => (
    <>
      {loading ? (
        <CircularProgress color="secondary" size={20} />
      ) : (
        <Button
          sx={{
            textTransform: "uppercase",
            borderRadius: "4px",
            color: "white !important",
            fontSize: "11px",
            width: "100px",
          }}
          variant="contained"
          size="small"
          disabled={loading}
          onClick={() => handleActionButtonClick(item)}
        >
          {item.status === "Unresolved" ? "Resolve" : "Reopen"}
        </Button>
      )}
    </>
  );

  const handleActionButtonClick = (item) => {
    setLoading(true);
    UpdateSupportStatus(
      dispatchData,
      {
        status: item.status === "Unresolved" ? "R" : null,
        id: item.id,
      },
      () => {
        setLoading(false);
        GetAllSupportItem(dispatchData, successAction, errorAction);
      },
      errorAction
    );
  };

  const errorAction = () => {
    setDataLoading(false);
    setLoading(false);
  };

  function getDataForDownload(supportData) {
    setDownloadData(null);
    const csvData = supportData.map((supportItem) => {
      return {
        "Support ID": supportItem.id,
        "Full Name": supportItem.fullName ? supportItem.fullName : "N/A",
        Subject: supportItem.subject ? supportItem.subject : "N/A",
        Message: supportItem.message ? supportItem.message : "N/A",
        "Posted On": moment(supportItem.postedOn).format("MM/DD/yyyy"),
        Status: supportItem.status ? supportItem.status : "N/A",
      };
    });

    setDownloadData(csvData);
  }

  const handleCSVDownload = () => {
    ShowSuccessMessage(dispatch, "Data exported successfully");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={3} mt={2}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              {(user_data?.customer?.roleId === 4 ||
                user_data?.customer?.roleId === 2) && (
                <Grid
                  item
                  xs={12}
                  xl={12}
                  sx={{ justifyContent: "end", display: "flex" }}
                >
                  <MDBox mr={3} mt={2}>
                    {(() => {
                      if (
                        downloadData &&
                        ((Array.isArray(downloadData) &&
                          downloadData.length > 0) ||
                          typeof downloadData === "string")
                      ) {
                        return (
                          <CSVLink
                            data={downloadData}
                            className="white"
                            asyncOnClick={false}
                            filename={`Support.csv`}
                            onClick={handleCSVDownload}
                          >
                            <MDButton className="btn-spc" color="success">
                              {!dataLoading ? (
                                <span>Export</span>
                              ) : (
                                <CircularProgress color="white" size={22} />
                              )}
                            </MDButton>
                          </CSVLink>
                        );
                      } else if (
                        Array.isArray(downloadData) &&
                        downloadData.length === 0
                      ) {
                        return <></>;
                      } else {
                        return (
                          <MDButton
                            className="btn-spc"
                            color="success"
                            sx={{ pointerEvents: "none" }}
                          >
                            <CircularProgress color="white" size={22} />
                          </MDButton>
                        );
                      }
                    })()}
                  </MDBox>
                </Grid>
              )}
              <MDBox pt={3} pb={3} display="flex" justifyContent="center">
                {dataLoading ? (
                  <CircularProgress color="success" size={30} />
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={true}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    canSearch={true}
                    noEndBorder
                    setGlobalFilterAPI={(value) => {
                      GetAllSupportItem(
                        dispatchData,
                        successAction,
                        errorAction,
                        value
                      );
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SupportRM;
